import type { Schemas } from "@norn/sdk"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Check, Play } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { Tooltip } from "@/components/ui/tooltip"
import { refusalOfError, run } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import type { Artifact } from "@/lib/defs"
import { useT } from "@/lib/i18n"
import { exactText, parseExact, stringifyExact } from "@/lib/lossless"
import { useCell } from "@/lib/session"
import { asString, isRecord } from "@/lib/utils"
import { usePrincipal } from "@/widget/runtime"
import { useObjectPages, useTitleOf } from "./hooks"
import { sentenceCase } from "./humanize"
import type { Ontology, TypeMeta } from "./ontology"
import { isMasked, type Masked, RefusalNote } from "./values"

interface PropertySchema {
  readonly type?: string
  readonly enum?: readonly unknown[]
  readonly description?: string
}

/** One admitted action this record can start: acting on it, or creating a record linked to it. */
export interface RecordCommand {
  readonly actionId: string
  readonly title: string
  readonly description?: string
  readonly mode: "record" | "create"
  readonly properties: readonly [string, PropertySchema][]
  readonly required: readonly string[]
  /** Input taken from this record (its key, link keys, current values for edits). */
  readonly preset: Readonly<Record<string, unknown>>
  /** Properties fixed by this record; shown, not editable. */
  readonly locked: readonly string[]
  /** Declared create links supply authorized record selectors, rather than free-text keys. */
  readonly references?: Readonly<Record<string, string>>
}

const MAX_COMMANDS = 6

/** Prefer declared ontology labels, with a plain label for any action-only field. */
export function commandFieldLabel(type: TypeMeta, id: string): string {
  return type.fields.find((field) => field.id === id)?.label ?? sentenceCase(id.replace(/^[a-z][a-z0-9]*__/, ""))
}

function commandOf(a: Artifact) {
  const p = a.payload
  const schema = isRecord(p.inputSchema) ? p.inputSchema : {}
  const derived = Array.isArray(p.derive) ? p.derive.filter(isRecord).filter((d) => d.inputRequired === false).map((d) => d.field) : []
  const properties = isRecord(schema.properties) ? Object.entries(schema.properties).filter(([id]) => !derived.includes(id)).map(([id, s]) => [id, (isRecord(s) ? s : {}) as PropertySchema] as [string, PropertySchema]) : []
  const required = Array.isArray(schema.required) ? schema.required.filter((r): r is string => typeof r === "string" && !derived.includes(r)) : []
  const actionId = asString(p.actionId) ?? a.artifactId
  const references = Object.fromEntries((Array.isArray(p.createLinks) ? p.createLinks : []).filter(isRecord).flatMap((l) => typeof l.targetIdField === "string" && typeof l.targetType === "string" ? [[l.targetIdField, l.targetType]] : []))
  return { actionId, title: asString(p.title) ?? sentenceCase(actionId.split(".").slice(-2).join(" ")), description: asString(p.description), properties, required, references }
}

/** Create-only business actions for a collection. Authorization remains at the cell's commit boundary. */
export function creationCommands(artifacts: readonly Artifact[], objectType: string): RecordCommand[] {
  return artifacts.filter((a) => {
    const p = a.payload
    const effect = isRecord(p.effectProgram) ? p.effectProgram : {}
    return a.kind === "action" && p.ledgerCategory === "business.demo" && effect.objectKind === objectType && effect.kind === "upsert_input_object" && isRecord(effect.writeMode) && effect.writeMode.kind === "create_only"
  }).map((a) => ({ ...commandOf(a), mode: "create" as const, preset: {}, locked: [] }))
}

export function CreateCommands({ scope, type, artifacts, ontology }: { scope: Scope; type: TypeMeta; artifacts: readonly Artifact[]; ontology?: Ontology }) {
  const principal = usePrincipal(scope)
  const commands = creationCommands(artifacts, type.name)
  const [active, setActive] = useState<RecordCommand | null>(null)
  const [receipt, setReceipt] = useState<{ title: string; accepted: Schemas["ActionAccepted"] } | null>(null)
  if (!commands.length || principal.status !== "ready") return null
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {commands.map((c) => <Button key={c.actionId} size="sm" onClick={() => setActive(c)}><Play aria-hidden />{c.title}</Button>)}
      </div>
      {receipt ? <Receipt scope={scope} title={receipt.title} accepted={receipt.accepted} /> : null}
      {active ? (
        <CommandDialog
          scope={scope} actor={principal.actor} type={type} command={active} ontology={ontology}
          onClose={() => setActive(null)}
          onCommitted={(accepted) => { setReceipt({ title: active.title, accepted }); setActive(null) }}
        />
      ) : null}
    </div>
  )
}

/**
 * Admitted actions a person can start from this record, from the ActionIR alone: transitions the
 * record can make now, edits (upserts that are not create-only) of this record, and creations whose
 * declared links name this record's type. Source projections and seeds belong to connectors and
 * importers, never to a person, so they are not offered.
 */
export function recordCommands(artifacts: readonly Artifact[], type: TypeMeta, key: string, values: Record<string, unknown>, masked: Masked): RecordCommand[] {
  const out: RecordCommand[] = []
  for (const a of artifacts) {
    if (a.kind !== "action") continue
    const p = a.payload
    const actionId = asString(p.actionId) ?? a.artifactId
    if (asString(p.ledgerCategory)?.startsWith("source.") || p.ledgerCategory === "showcase.synthetic" || actionId.endsWith(".seed_projection")) continue
    const effect = isRecord(p.effectProgram) ? p.effectProgram : {}
    const base = commandOf(a)
    const { properties } = base
    const kind = asString(effect.kind)
    const idField = asString(effect.objectIdField)
    const createOnly = isRecord(effect.writeMode) && effect.writeMode.kind === "create_only"
    if (asString(effect.objectKind) === type.name && idField && (kind === "fire_transition" || (kind === "upsert_input_object" && !createOnly))) {
      if (kind === "fire_transition" && type.lifecycle) {
        const transition = type.lifecycle.transitions.find((tr) => tr.id === effect.transition)
        const current = values[type.lifecycle.property]
        if (transition && typeof current === "string" && !transition.from.includes(current)) continue
      }
      const preset: Record<string, unknown> = { [idField]: key }
      if (kind === "upsert_input_object")
        for (const [id] of properties) if (id !== idField && values[id] !== undefined && values[id] !== null && !isMasked(masked, id)) preset[id] = values[id]
      out.push({ ...base, mode: "record", preset, locked: [idField] })
      continue
    }
    if (kind === "upsert_input_object" && createOnly && Array.isArray(p.createLinks)) {
      const links = p.createLinks.filter(isRecord).filter((l) => l.targetType === type.name && typeof l.targetIdField === "string")
      if (!links.length) continue
      const preset = Object.fromEntries(links.map((l) => [l.targetIdField as string, key]))
      out.push({ ...base, mode: "create", preset, locked: Object.keys(preset) })
    }
  }
  return out.sort((a, b) => Number(a.mode === "create") - Number(b.mode === "create")).slice(0, MAX_COMMANDS)
}

type Explanation = Schemas["PermissionExplainResponse"]

/** Explain the cell's first failed check without inferring a cause from the person's role. */
function commandDenial(explanation: Explanation, t: ReturnType<typeof useT>): string {
  // The stable reason names the production refusal. Shadow evaluation can add a later denied
  // conjunct without changing that reason, so known codes take priority over the conjunct list.
  switch (explanation.reason) {
    case "authz.deny.invalid_request": return t("ws.cmd.deniedInvalidRequest")
    case "authz.deny.stale_principal_epoch": return t("ws.cmd.deniedStaleAccess")
    case "action.target_not_found": return t("ws.cmd.deniedTargetUnavailable")
    case "authz.deny.absent_scope": return t("ws.cmd.deniedScope")
    case "authz.deny.capability": return t("ws.cmd.deniedCapability")
    case "authz.deny.delegation": return t("ws.cmd.deniedDelegation")
    case "authz.deny.access_error": return t("ws.cmd.deniedEvaluation")
    case "authz.deny.access":
    case "authz.no_policy": return t("ws.cmd.deniedHelp")
  }
  if (explanation.reason.startsWith("identity.membership.") || explanation.reason.startsWith("identity.delegation.")) return t("ws.cmd.deniedManagement")
  if (explanation.reason.startsWith("delegation.") || explanation.reason.startsWith("authz.deny.delegation_")) return t("ws.cmd.deniedDelegation")
  const gate = (["scope", "capability", "delegation", "management", "access"] as const)
    .find((key) => explanation.conjuncts[key] === "deny" || explanation.conjuncts[key] === "error")
  if (gate && explanation.conjuncts[gate] === "error") return t("ws.cmd.deniedEvaluation")
  switch (gate) {
    case "scope": return t("ws.cmd.deniedScope")
    case "capability": return t("ws.cmd.deniedCapability")
    case "delegation": return t("ws.cmd.deniedDelegation")
    case "management": return t("ws.cmd.deniedManagement")
    default: return t("ws.cmd.deniedHelp")
  }
}

/** The cell's own answer for each command on this record: the admitted action under Cedar, for this principal. */
function useCommandPermissions(scope: Scope, actor: string | null, objectType: string, key: string, version: number, commands: readonly RecordCommand[]) {
  const { connection, client } = useCell()
  return useQueries({
    // Explain needs an existing resource: only commands on this record can be checked before commit.
    queries: commands.filter((c) => c.mode === "record").map((c) => ({
      queryKey: ["record-command-explain", connection.baseUrl, scope.orgId, scope.environmentId, actor, c.actionId, objectType, key, version],
      enabled: Boolean(actor),
      staleTime: 30_000,
      retry: false,
      queryFn: async () => {
        const body = stringifyExact({ scope: { orgId: scope.orgId, environmentId: scope.environmentId }, principal: actor, action: c.actionId, resource: { objectType, key }, input: c.preset, delegationId: null })
        const text = await run(connection.baseUrl, () => client.POST("/v1/authz/explain", { body: {} as never, bodySerializer: () => body, parseAs: "text" }))
        return parseExact(text) as Explanation
      },
    })),
  })
}

/** Actions on a record: enabled only when the cell allows them for this principal, run behind a confirm with a generated form. */
export function RecordCommands({ scope, actor, type, objectKey, version, values, masked, artifacts, onCommitted }: {
  scope: Scope
  actor: string | null
  type: TypeMeta
  objectKey: string
  version: number
  values: Record<string, unknown>
  masked: Masked
  artifacts: readonly Artifact[]
  onCommitted?: (accepted: Schemas["ActionAccepted"], command: RecordCommand) => void
}) {
  const t = useT()
  const commands = recordCommands(artifacts, type, objectKey, values, masked)
  const permissions = useCommandPermissions(scope, actor, type.name, objectKey, version, commands)
  const [active, setActive] = useState<RecordCommand | null>(null)
  const [receipt, setReceipt] = useState<{ title: string; accepted: Schemas["ActionAccepted"] } | null>(null)
  if (!commands.length || !actor) return null
  const onRecord = commands.filter((c) => c.mode === "record")
  const reasonOf = (c: RecordCommand): string | null => {
    // A new record does not exist to explain against; the cell authorizes it at commit, and says so.
    if (c.mode === "create") return null
    const q = permissions[onRecord.indexOf(c)]
    if (!q || q.isPending) return t("ws.cmd.checking")
    if (q.isError) {
      const refused = refusalOfError(q.error)
      return t("ws.cmd.unavailable", { reason: refused ? `${refused.refusal.code}: ${refused.refusal.explanation}` : String(q.error) })
    }
    return q.data.decision === "allow" ? null : commandDenial(q.data, t)
  }
  const reasons = commands.map(reasonOf)
  const settled = permissions.every((q) => !q.isPending)
  const recordReasons = reasons.filter((_, i) => commands[i]?.mode === "record")
  const firstDenied = recordReasons.find((r) => r !== null)
  const allExplainedDenials = permissions.length > 0 && permissions.every((permission) => permission.isSuccess && permission.data.decision === "deny")
  return (
    <section aria-label={t("ws.cmd.title")} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {commands.map((c, i) => {
          const reason = reasons[i]
          const button = (
            <Button size="sm" variant={c.mode === "record" ? "outline" : "ghost"} disabled={reason !== null} onClick={() => setActive(c)}>
              <Play className="rtl:-scale-x-100" /> {c.title}
            </Button>
          )
          return reason === null ? (
            c.mode === "create" ? (
              <Tooltip key={c.actionId} content={t("ws.cmd.checkedAtCommit")}>
                <span>{button}</span>
              </Tooltip>
            ) : (
              <span key={c.actionId}>{button}</span>
            )
          ) : (
            <Tooltip key={c.actionId} content={reason}>
              <span tabIndex={0} className="rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/30">
                {button}
              </span>
            </Tooltip>
          )
        })}
      </div>
      {settled && recordReasons.length > 0 && recordReasons.every((r) => r !== null) && firstDenied && !allExplainedDenials ? (
        <p className="text-xs text-muted-foreground">{t("ws.cmd.readOnly", { actor, reason: firstDenied })}</p>
      ) : null}
      {onRecord.map((command, index) => {
        const permission = permissions[index]
        if (!permission?.isSuccess || permission.data.decision !== "deny") return null
        return (
          <details key={command.actionId} className="rounded-lg border border-border px-3 py-2 text-xs">
            <summary className="cursor-pointer font-medium">{t("ws.cmd.whyDenied", { action: command.title })}</summary>
            <p className="mt-2 text-muted-foreground">{commandDenial(permission.data, t)}</p>
            <p className="mt-1 text-subtle-foreground">{t("permissions.explain.stableReasonCode")}: <code dir="ltr">{permission.data.reason}</code></p>
            {command.description ? <p className="mt-1 text-muted-foreground">{command.description}</p> : null}
          </details>
        )
      })}
      {receipt ? <Receipt scope={scope} title={receipt.title} accepted={receipt.accepted} /> : null}
      {active ? (
        <CommandDialog
          key={active.actionId}
          scope={scope}
          actor={actor}
          type={type}
          command={active}
          onClose={() => setActive(null)}
          onCommitted={(accepted) => {
            setReceipt({ title: active.title, accepted })
            onCommitted?.(accepted, active)
            setActive(null)
          }}
        />
      ) : null}
    </section>
  )
}

export function Receipt({ scope, title, accepted }: { scope: Scope; title: string; accepted: Schemas["ActionAccepted"] }) {
  const t = useT()
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-[var(--success)]/40 px-3 py-2 text-sm">
      <span className="inline-flex items-center gap-1.5 font-medium text-[var(--success)]">
        <Check className="size-4" aria-hidden /> {t("ws.cmd.committed", { seq: String(accepted.receipt.seq) })}
      </span>
      <span className="text-muted-foreground">{title}</span>
      <Link to="/$orgId/$environmentId/ledger" params={scope} search={{ commit: String(accepted.receipt.seq), hash: accepted.receiptHash }} className="text-xs underline underline-offset-4">{t("ws.cmd.receipt")}</Link>
      {accepted.replayed ? <span className="text-xs text-muted-foreground">{t("ws.cmd.replayed")}</span> : null}
      {accepted.receipt.written
        .filter((w) => !w.objectType.startsWith("norn."))
        .map((w) => (
          <Link key={`${w.objectType}/${w.key}`} to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: w.objectType, key: w.key }} dir="ltr" className="font-mono text-xs underline underline-offset-4">
            {w.objectType} {w.key} · v{w.version}
          </Link>
        ))}
    </div>
  )
}

// JSON number grammar: no leading zeros, so every accepted text parses.
const NUMBER = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?$/
const INTEGER = /^-?(0|[1-9]\d*)$/

function initial(value: unknown): string | boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "string") return value
  if (value === undefined || value === null) return ""
  return exactText(value) ?? (typeof value === "number" ? String(value) : stringifyExact(value))
}

/** The exact input a form produces, or the first field it cannot read. */
export function buildInput(command: RecordCommand, form: Record<string, string | boolean>): { input: Record<string, unknown> } | { invalid: string } {
  const input: Record<string, unknown> = {}
  for (const [id, schema] of command.properties) {
    if (command.locked.includes(id)) {
      input[id] = command.preset[id]
      continue
    }
    const raw = form[id]
    if (schema.type === "boolean") {
      if (typeof raw === "boolean") input[id] = raw
      continue
    }
    const text = typeof raw === "string" ? raw.trim() : ""
    if (!text) {
      if (command.required.includes(id)) return { invalid: id }
      // Clearing a value this record had is an edit; an untouched empty field is simply not sent.
      if (command.preset[id] !== undefined && (schema.type === "string" || schema.type === undefined)) input[id] = ""
      continue
    }
    if (schema.type === "integer" || schema.type === "number") {
      if (!(schema.type === "integer" ? INTEGER : NUMBER).test(text)) return { invalid: id }
      try {
        input[id] = parseExact(text)
      } catch {
        return { invalid: id }
      }
    } else if (schema.type === "object" || schema.type === "array") {
      try {
        input[id] = parseExact(text)
      } catch {
        return { invalid: id }
      }
    } else input[id] = text
  }
  return { input }
}

export function CommandDialog({ scope, actor, type, command, ontology, onClose, onCommitted }: {
  scope: Scope
  actor: string
  type: TypeMeta
  command: RecordCommand
  ontology?: Ontology
  onClose: () => void
  onCommitted: (accepted: Schemas["ActionAccepted"]) => void
}) {
  const t = useT()
  const { connection, client } = useCell()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(command.properties.map(([id, schema]) => [
      id,
      // Untouched optional booleans stay unsent (""), so an edit never overwrites a value it could not read.
      schema.type === "boolean" && (typeof command.preset[id] === "boolean" || command.required.includes(id)) ? command.preset[id] === true : initial(command.preset[id]),
    ])),
  )
  // One key per dialog: a retried submit is an exact replay, never a second commit.
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const built = buildInput(command, form)
  const commit = useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const body = stringifyExact({ scope: { orgId: scope.orgId, environmentId: scope.environmentId }, actor, input, idempotencyKey })
      const text = await run(connection.baseUrl, () => client.POST("/v1/actions/{action}", { params: { path: { action: command.actionId } }, body: {} as never, bodySerializer: () => body, parseAs: "text" }))
      return parseExact(text) as Schemas["ActionAccepted"]
    },
    onSuccess: (accepted) => {
      // The record, its Why, provenance, links and every list re-read from the cell.
      void queryClient.invalidateQueries()
      onCommitted(accepted)
    },
  })
  const label = (id: string) => commandFieldLabel(type, id)
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open && !commit.isPending) onClose()
      }}
      size="lg"
      title={command.title}
      description={command.mode === "create" ? `${t("ws.cmd.confirm", { actor })} ${t("ws.cmd.checkedAtCommit")}` : t("ws.cmd.confirm", { actor })}
      confirmLabel={t("ws.cmd.commit")}
      pendingLabel={t("ws.cmd.committing")}
      cancelLabel={t("common.cancel")}
      pending={commit.isPending}
      confirmDisabled={"invalid" in built}
      onConfirm={() => {
        if ("input" in built) commit.mutate(built.input)
      }}
    >
      <div className="mt-4 max-h-[55vh] space-y-3 overflow-y-auto pe-1">
        {command.description ? <p className="text-xs text-muted-foreground">{command.description}</p> : null}
        {command.properties.map(([id, schema]) => {
          const locked = command.locked.includes(id)
          const required = command.required.includes(id)
          const invalid = "invalid" in built && built.invalid === id && (form[id] !== "" || !required)
          const value = form[id]
          return (
            <label key={id} className="block space-y-1 text-sm">
              <span className="flex items-baseline gap-2">
                <span className="font-medium">{label(id)}</span>
                {locked ? <span className="text-2xs text-muted-foreground">{t("ws.cmd.fixed")}</span> : required ? <span className="text-2xs text-muted-foreground">{t("ws.cmd.required")}</span> : null}
              </span>
              {command.references?.[id] && !locked ? (
                <ReferenceSelect scope={scope} objectType={command.references[id]} ontology={ontology} label={label(id)} value={typeof value === "string" ? value : ""} onChange={(next) => setForm((f) => ({ ...f, [id]: next }))} />
              ) : schema.type === "boolean" ? (
                <input type="checkbox" aria-label={label(id)} checked={value === true} disabled={locked} onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.checked }))} className="size-4" />
              ) : schema.enum?.length ? (
                <select
                  aria-label={label(id)}
                  value={typeof value === "string" ? value : ""}
                  disabled={locked}
                  onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
                >
                  <option value="">—</option>
                  {schema.enum.map((o) => (
                    <option key={String(o)} value={String(o)}>
                      {String(o)}
                    </option>
                  ))}
                </select>
              ) : schema.type === "object" || schema.type === "array" ? (
                <textarea
                  aria-label={label(id)}
                  value={typeof value === "string" ? value : ""}
                  readOnly={locked}
                  dir="ltr"
                  onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                  className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-xs outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
                />
              ) : (
                <input
                  aria-label={label(id)}
                  value={typeof value === "string" ? value : ""}
                  readOnly={locked}
                  dir="auto"
                  inputMode={schema.type === "integer" || schema.type === "number" ? "decimal" : undefined}
                  aria-invalid={invalid || undefined}
                  onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none read-only:bg-muted/50 read-only:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive"
                />
              )}
              {invalid ? <span className="text-2xs text-destructive">{schema.type === "object" || schema.type === "array" ? t("ws.cmd.invalidJson") : t("ws.cmd.invalidNumber")}</span> : null}
            </label>
          )
        })}
        {commit.isError ? <RefusalNote error={commit.error} title={t("ws.cmd.refused")} /> : null}
      </div>
    </ConfirmDialog>
  )
}

function ReferenceSelect({ scope, objectType, ontology, label, value, onChange }: { scope: Scope; objectType: string; ontology: Ontology | undefined; label: string; value: string; onChange: (value: string) => void }) {
  const t = useT()
  const pages = useObjectPages(scope, objectType)
  const titleOf = useTitleOf(ontology)
  return <span className="block space-y-1">
    <select aria-label={label} value={value} disabled={pages.isPending || pages.isError} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
      <option value="">{t("ws.cmd.chooseRecord")}</option>
      {pages.data?.pages.flatMap((p) => p.items).map((o) => <option key={o.key} value={o.key}>{titleOf(o, o.key).title} · {o.key}</option>)}
    </select>
    {pages.hasNextPage ? <Button size="sm" variant="ghost" disabled={pages.isFetchingNextPage} onClick={() => void pages.fetchNextPage()}>{t("ws.list.loadMore")}</Button> : null}
    {pages.isError ? <RefusalNote error={pages.error} title={t("ws.cmd.refused")} /> : null}
  </span>
}
